/** Mo=0 … So=6 (plantafel weeks start on Monday). */
export const PLANTAFEL_WEEKDAY_LABELS = [
  'Montag',
  'Dienstag',
  'Mittwoch',
  'Donnerstag',
  'Freitag',
  'Samstag',
  'Sonntag',
] as const;

export const PLANTAFEL_WEEKDAY_LABELS_SHORT = ['MO', 'DI', 'MI', 'DO', 'FR', 'SA', 'SO'] as const;

/** Mon–Sat; Sunday off by default. */
export const DEFAULT_PLANTAFEL_VISIBLE_WEEKDAYS: readonly number[] = [0, 1, 2, 3, 4, 5];

export const DEFAULT_PLANTAFEL_DAY_HOUR_START = 6;
export const DEFAULT_PLANTAFEL_DAY_HOUR_END = 18;

export const PLANTAFEL_DEFAULT_ACTIVITY_DURATION_PRESETS = [15, 30, 45, 60, 90, 120] as const;
export const DEFAULT_PLANTAFEL_ACTIVITY_DURATION_MINUTES = 60;

export type PlantafelVisibleWeekdays = number[];

export type PlantafelDayHours = {
  hourStart: number;
  hourEnd: number;
};

export type PlantafelDisplaySettings = {
  visibleWeekdays: PlantafelVisibleWeekdays;
  dayHours: PlantafelDayHours;
  defaultActivityDurationMinutes: number;
};

export const DEFAULT_PLANTAFEL_DISPLAY_SETTINGS: PlantafelDisplaySettings = {
  visibleWeekdays: [...DEFAULT_PLANTAFEL_VISIBLE_WEEKDAYS],
  dayHours: { hourStart: DEFAULT_PLANTAFEL_DAY_HOUR_START, hourEnd: DEFAULT_PLANTAFEL_DAY_HOUR_END },
  defaultActivityDurationMinutes: DEFAULT_PLANTAFEL_ACTIVITY_DURATION_MINUTES,
};

function parseHour(raw: unknown, fallback: number): number {
  const n = typeof raw === 'number' ? raw : parseInt(String(raw ?? ''), 10);
  if (!Number.isInteger(n)) return fallback;
  return Math.max(0, Math.min(24, n));
}

export function normalizePlantafelVisibleWeekdays(raw: unknown): PlantafelVisibleWeekdays {
  if (!Array.isArray(raw)) return [...DEFAULT_PLANTAFEL_VISIBLE_WEEKDAYS];
  const values = raw
    .map((x) => (typeof x === 'number' ? x : parseInt(String(x), 10)))
    .filter((n) => Number.isInteger(n) && n >= 0 && n <= 6);
  const unique = [...new Set(values)].sort((a, b) => a - b);
  return unique.length > 0 ? unique : [...DEFAULT_PLANTAFEL_VISIBLE_WEEKDAYS];
}

export function normalizePlantafelDayHours(
  hourStartRaw?: unknown,
  hourEndRaw?: unknown,
): PlantafelDayHours {
  let hourStart = parseHour(hourStartRaw, DEFAULT_PLANTAFEL_DAY_HOUR_START);
  let hourEnd = parseHour(hourEndRaw, DEFAULT_PLANTAFEL_DAY_HOUR_END);
  if (hourEnd <= hourStart) hourEnd = Math.min(24, hourStart + 1);
  if (hourEnd - hourStart < 1) {
    return { hourStart: DEFAULT_PLANTAFEL_DAY_HOUR_START, hourEnd: DEFAULT_PLANTAFEL_DAY_HOUR_END };
  }
  return { hourStart, hourEnd };
}

export function normalizePlantafelDefaultActivityDurationMinutes(raw: unknown): number {
  const n = typeof raw === 'number' ? raw : parseInt(String(raw ?? ''), 10);
  if (!Number.isInteger(n)) return DEFAULT_PLANTAFEL_ACTIVITY_DURATION_MINUTES;
  if ((PLANTAFEL_DEFAULT_ACTIVITY_DURATION_PRESETS as readonly number[]).includes(n)) return n;
  return DEFAULT_PLANTAFEL_ACTIVITY_DURATION_MINUTES;
}

export function normalizePlantafelDisplaySettings(raw: {
  visible_weekdays?: unknown;
  day_hour_start?: unknown;
  day_hour_end?: unknown;
  default_activity_duration_minutes?: unknown;
} | null | undefined): PlantafelDisplaySettings {
  return {
    visibleWeekdays: normalizePlantafelVisibleWeekdays(raw?.visible_weekdays),
    dayHours: normalizePlantafelDayHours(raw?.day_hour_start, raw?.day_hour_end),
    defaultActivityDurationMinutes: normalizePlantafelDefaultActivityDurationMinutes(
      raw?.default_activity_duration_minutes,
    ),
  };
}

export function plantafelHourSpan(dayHours: PlantafelDayHours): number {
  return dayHours.hourEnd - dayHours.hourStart;
}

/** Map block hours to visible range; pin before/after viewport to start/end. */
export function clipBlockHoursToViewport(
  blockHourStart: number,
  blockHourEnd: number,
  dayHours: PlantafelDayHours,
  minSpanHours = 0.25,
): { visStart: number; visEnd: number } {
  const { hourStart: v0, hourEnd: v1 } = dayHours;
  if (blockHourEnd <= v0) {
    return { visStart: v0, visEnd: Math.min(v1, v0 + minSpanHours) };
  }
  if (blockHourStart >= v1) {
    return { visStart: Math.max(v0, v1 - minSpanHours), visEnd: v1 };
  }
  const visStart = Math.max(blockHourStart, v0);
  const visEnd = Math.min(blockHourEnd, v1);
  if (visEnd <= visStart) {
    return { visStart: v0, visEnd: Math.min(v1, v0 + minSpanHours) };
  }
  return { visStart, visEnd };
}

export function hourFractionInViewport(hour: number, dayHours: PlantafelDayHours): number {
  const { hourStart, hourEnd } = dayHours;
  const span = hourEnd - hourStart;
  if (span <= 0) return 0;
  if (hour <= hourStart) return 0;
  if (hour >= hourEnd) return 1;
  return (hour - hourStart) / span;
}

export function togglePlantafelWeekday(current: PlantafelVisibleWeekdays, weekday: number): PlantafelVisibleWeekdays {
  if (current.includes(weekday)) {
    const next = current.filter((d) => d !== weekday);
    return next.length > 0 ? next : [...DEFAULT_PLANTAFEL_VISIBLE_WEEKDAYS];
  }
  return [...current, weekday].sort((a, b) => a - b);
}
